'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';
import type { ClinicAskOwnerParty, ClinicAskStatus } from '../../types/clinic-access';

/**
 * The open loops in a conversation: what is still outstanding, and why it is here.
 *
 * Deliberately a list of hairline rows, not cards. Four identical rounded cards with four
 * identical shadows makes every item feel equally urgent, which is the opposite of what
 * this panel is for — the whole point is that a conversation with two open requests reads
 * as smaller than one with ten, and cards flatten that.
 *
 * Closed and dismissed Asks are collapsed behind a single line. They are context, not
 * work, and a conversation that has been used properly accumulates far more of them than
 * it has open loops — showing them by default would bury the two things that matter.
 *
 * Composes rather than renders its own work items: a linked Task is handed to the
 * consumer through `renderTask`, so the canonical Task renderer draws it and this panel
 * never becomes a second place a Task is displayed.
 */

export interface StillOpenAsk {
  id: number;
  summary: string;
  /** The verbatim quotation. Null when the reader lacks `view_content`. */
  excerpt: string | null;
  status: ClinicAskStatus;
  expectedOwnerParty: ClinicAskOwnerParty | null;
  /** True when every linked Task is finished and someone should confirm the Ask. */
  eligibleForClosureReview: boolean;
  sourceMessage: {
    id: number;
    providerMessageId: string;
    fromName: string | null;
    fromEmail: string | null;
    sentAt: number;
  } | null;
  linkedTasks: Array<{ taskId: number; label: string; status: string | null }>;
}

export interface StillOpenPanelProps {
  asks: StillOpenAsk[];
  /** Scrolls the timeline to this Ask's source message and highlights it. */
  onSelectAsk: (ask: StillOpenAsk) => void;
  /** The Ask whose source message is currently highlighted, if any. */
  selectedAskId?: number | null;
  /** Draws a linked Task through the canonical Task renderer. */
  renderTask?: (taskId: number) => React.ReactNode;
  className?: string;
}

export function StillOpenPanel({
  asks,
  onSelectAsk,
  selectedAskId,
  renderTask,
  className,
}: StillOpenPanelProps) {
  const [showHistory, setShowHistory] = useState(false);

  const open = asks.filter((ask) => ask.status === 'open');
  const settled = asks.filter((ask) => ask.status !== 'open');

  return (
    <section className={cn('space-y-3', className)}>
      <h4 className="font-display text-sm font-normal text-foreground">Still open</h4>

      {open.length === 0 ? (
        // An invitation, not a celebration, and never an empty queue with a zero in it.
        // A count is the anxiety; "nothing here" is the reassurance.
        <p className="text-sm leading-relaxed text-murmur">Nothing here is waiting on anyone.</p>
      ) : (
        <ul className="space-y-0">
          {open.map((ask) => (
            <li key={ask.id} className="border-b border-mineral last:border-b-0">
              <button
                type="button"
                onClick={() => onSelectAsk(ask)}
                className={cn(
                  'w-full py-3 text-left transition-colors',
                  selectedAskId === ask.id && 'bg-muted/40'
                )}
              >
                <p className="text-sm leading-relaxed text-foreground">{ask.summary}</p>

                <HereBecause ask={ask} />

                {ask.linkedTasks.length > 0 && renderTask && (
                  <div className="mt-2 space-y-2">
                    {ask.linkedTasks.map((task) => (
                      <div key={task.taskId}>{renderTask(task.taskId)}</div>
                    ))}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {settled.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowHistory((shown) => !shown)}
            className="text-xs text-murmur underline-offset-4 hover:underline"
          >
            {showHistory ? 'Hide what was handled' : `${settled.length} already handled here`}
          </button>

          {showHistory && (
            <ul className="mt-2 space-y-0">
              {settled.map((ask) => (
                <li
                  key={ask.id}
                  className="border-b border-mineral py-2 last:border-b-0 text-sm text-murmur"
                >
                  {ask.summary}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The one short clause naming why this item is here.
 *
 * Honey, because this is the system explaining a decision it made — one of that colour's
 * three jobs. A default with no visible reason is a black box, and black boxes raise
 * arousal in exactly the place this product is trying to lower it.
 */
function HereBecause({ ask }: { ask: StillOpenAsk }) {
  const reason = hereBecauseReason(ask);
  if (!reason) return null;

  return (
    <p className="mt-1 text-xs leading-relaxed text-murmur">
      <span className="text-honey">Here because</span> {reason}
    </p>
  );
}

export function hereBecauseReason(ask: StillOpenAsk): string | null {
  if (ask.eligibleForClosureReview) {
    // Finished work is a reason to look, not a reason to clear. Saying so is what keeps
    // the user in charge of the decision.
    return 'the work linked to this is finished — worth confirming it is settled.';
  }

  if (ask.expectedOwnerParty === 'team') {
    return ask.sourceMessage
      ? `${ask.sourceMessage.fromName || ask.sourceMessage.fromEmail || 'someone'} asked for something and nobody has answered.`
      : 'this is waiting on someone here.';
  }

  if (ask.expectedOwnerParty === 'counterparty') {
    return 'you asked for something and it has not come back.';
  }

  return 'it is not clear yet who this is waiting on.';
}
